import { InputText, InputTextType } from 'src/components/atoms/InputText';
import ButtonIcon from 'src/components/molecules/ButtonIcon';
import styled from 'styled-components/macro';
import { ChipTone } from './helpers/listCheckHelpers';

// Interfaces
interface ContainerProps {
  isMainSidebarOpen: boolean;
}

export const Container = styled.div<ContainerProps>`
  position: fixed;
  top: 0;
  left: ${({ isMainSidebarOpen }) => (isMainSidebarOpen ? '250px' : '80px')};
  right: 0;
  bottom: 0;
  display: flex;
  height: 100vh;
  width: ${({ isMainSidebarOpen }) =>
    isMainSidebarOpen ? 'calc(100vw - 250px)' : 'calc(100vw - 80px)'};
  background: ${({ theme }) => theme.color.gray[100]};
  z-index: 1;
`;

export const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  border-bottom: 1px solid ${({ theme }) => theme.color.gray[200]};
`;

export const StatusContainer = styled.div`
  display: flex;
  align-items: center;
`;

export const ReviewTitle = styled.h2`
  color: ${({ theme }) => theme.color.gray[500]};
  font-family: ${({ theme }) => theme.font.normal};
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  line-height: 145%;
  margin: 0;
`;

export const DocumentsText = styled.p`
  color: ${({ theme }) => theme.color.gray[500]};
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%;
  margin: 0;
  font-family: ${({ theme }) => theme.font.normal};
  margin-bottom: 20px;
`;

export const FolderSection = styled.div`
  padding-top: 16px;
`;

export const FolderHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 8px;
`;

export const FolderNameSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  flex: 1;
  min-width: 0;
`;

export const EditFormContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
`;

export const IconContainer = styled.div`
  width: 20px;
  height: 20px;
`;

export const IconText = styled.span`
  color: ${({ theme }) => theme.color.white};
  font-size: 10px;
  font-weight: 600;
`;

export const FolderName = styled.span`
  color: ${({ theme }) => theme.color.gray[500]};
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%;
`;

export const EditButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.color.blue[500]};
  font-size: 12px;
  font-style: normal;
  font-weight: 600;
  line-height: 145%;
  text-decoration-line: underline;
  text-decoration-style: solid;
  text-decoration-skip-ink: auto;
  text-decoration-thickness: auto;
  text-underline-offset: auto;
  text-underline-position: from-font;
  cursor: pointer;
  padding: 0;
`;

export const StyledInputText = styled(InputText).attrs((attrs) => ({
  type: InputTextType.text,
  showLabel: false,
  showOptionalLabel: false
}))`
  & > div {
    margin-bottom: 0px;
    margin-right: 4px;
    flex: 1;

    & > input {
      height: 1.5rem;
      width: 100%;
      max-width: 160px;
    }
  }
`;

export const StyledIconButtonBlue = styled(ButtonIcon)`
  border: 1px solid ${({ theme }) => theme.color.blue[700]};
  width: 32px;
  height: 32px;
  padding: 6px;
  margin-left: 4px;
  flex-shrink: 0;
`;

export const StyledIconButtonRed = styled(StyledIconButtonBlue)`
  border: 1px solid ${({ theme }) => theme.color.red[700]};
`;

export const FolderInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

export const FolderInfoSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

export const FolderInfoText = styled.div`
  color: ${({ theme }) => theme.color.gray[500]};
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%;
`;

export const FolderTimestamp = styled.div`
  color: ${({ theme }) => theme.color.gray[400]};
  font-size: 14px;
  line-height: 145%;
  cursor: pointer;
`;

export const LeftPanel = styled.div`
  background: ${({ theme }) => theme.color.white};
  border-right: 1px solid ${({ theme }) => theme.color.gray[300]};
  position: relative;
  width: 320px;
  max-width: 400px;
  min-width: 240px;
  height: 100vh;
  min-height: 600px;
  padding-top: 24px;
  padding-right: 36px;
  padding-bottom: 24px;
  padding-left: 36px;
  display: flex;
  flex-direction: column;

  @media (max-width: 1200px) {
    width: 280px;
    min-width: 240px;
  }

  @media (max-width: 768px) {
    width: 260px;
    min-width: 220px;
    padding-right: 20px;
    padding-left: 20px;
  }
`;

export const RightPanel = styled.div`
  background: ${({ theme }) => theme.color.white};
  flex: 1;
  display: flex;
  flex-direction: column;
  min-width: 600px;
  max-width: none;

  @media (max-width: 1200px) {
    min-width: 500px;
  }

  @media (max-width: 768px) {
    min-width: 400px;
  }
`;

export const OfferLetterPanel = styled.div`
  background: ${({ theme }) => theme.color.white};
  border-left: 1px solid ${({ theme }) => theme.color.gray[300]};
  width: 460px;
  min-width: 460px;
  height: 100vh;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
`;

export const OfferLetterPanelHeader = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.color.gray[200]};
  flex-shrink: 0;
`;

export const OfferLetterPanelTitle = styled.h2`
  color: ${({ theme }) => theme.color.gray[500]};
  font-family: ${({ theme }) => theme.font.normal};
  font-size: 18px;
  font-weight: 600;
  line-height: 145%;
  margin: 0;
`;

export const OfferLetterPanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 16px 24px;
`;

export const OfferLetterStepSection = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.color.gray[200]};
  &:last-child {
    border-bottom: none;
  }
`;

export const OfferLetterStepHeader = styled.button<{ $disabled?: boolean }>`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 0;
  background: none;
  border: none;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
`;

export const OfferLetterStepHeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const OfferLetterStepNumber = styled.span`
  color: ${({ theme }) => theme.color.gray[300]};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
`;

export const OfferLetterStepTitle = styled.span`
  color: ${({ theme }) => theme.color.gray[500]};
  font-size: 14px;
  font-weight: 600;
`;

export const OfferLetterStepHeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const OfferLetterStatusBadge = styled.span<{ $status: string }>`
  font-size: 12px;
  font-weight: 500;
  padding: 2px 8px;
  border-radius: 12px;
  ${({ $status, theme }) => {
    switch ($status) {
      case 'passed':
        return `background: ${theme.color.green[150]}; color: ${theme.color.green[800]};`;
      case 'failed':
        return `background: ${theme.color.red[100]}; color: ${theme.color.red[800]};`;
      case 'in_progress':
        return `background: ${theme.color.blue[100]}; color: ${theme.color.blue[800]};`;
      default:
        return `background: ${theme.color.gray[100]}; color: ${theme.color.gray[300]};`;
    }
  }}
`;

export const OfferLetterChevron = styled.img<{ $expanded: boolean }>`
  width: 16px;
  height: 16px;
  transform: ${({ $expanded }) => ($expanded ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s;
`;

export const OfferLetterStepBody = styled.div`
  padding-bottom: 12px;
`;

export const OfferLetterCheckItemRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 0;
  gap: 12px;
`;

export const OfferLetterCheckItemInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

export const OfferLetterCheckItemLabel = styled.div`
  color: ${({ theme }) => theme.color.gray[500]};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const OfferLetterEmailRow = styled.div`
  padding-top: 8px;
`;

export const OfferLetterTooltipWrapper = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;
`;

export const OfferLetterTooltipIcon = styled.button`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 1px solid ${({ theme }) => theme.color.gray[300]};
  background: ${({ theme }) => theme.color.gray[100]};
  color: ${({ theme }) => theme.color.gray[400]};
  font-size: 11px;
  font-weight: 600;
  cursor: help;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  flex-shrink: 0;

  &:hover + div {
    opacity: 1;
    visibility: visible;
  }
`;

export const OfferLetterTooltipContent = styled.div`
  position: absolute;
  top: calc(100% + 6px);
  left: 50%;
  transform: translateX(-50%);
  background: #1f2937;
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
  width: 8rem;
  max-width: 520px;
  white-space: normal;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.15s, visibility 0.15s;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    bottom: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-bottom-color: #1f2937;
  }
`;

export const OfferLetterCheckItemCriteria = styled.div`
  color: ${({ theme }) => theme.color.gray[300]};
  font-size: 12px;
  line-height: 1.4;
  margin-top: 2px;
`;

export const OfferLetterCheckItemButtons = styled.div<{ alignEnd?: boolean }>`
  display: flex;
  gap: 4px;
  flex-shrink: 0;
  align-self: ${({ alignEnd }) => (alignEnd ? 'flex-end' : '')};
`;

export const OfferLetterPassFailButton = styled.button<{
  $variant: 'pass' | 'fail';
  $selected: boolean;
}>`
  width: 32px;
  height: 32px;
  border-radius: 4px;
  border: 1px solid
    ${({ theme, $variant, $selected }) => {
      if (!$selected) return theme.color.gray[250];
      return $variant === 'pass' ? theme.color.green[500] : theme.color.red[500];
    }};
  background: ${({ theme, $variant, $selected }) => {
    if (!$selected) return 'white';
    return $variant === 'pass' ? theme.color.green[150] : theme.color.red[100];
  }};
  color: ${({ theme, $variant, $selected }) => {
    if (!$selected) return theme.color.gray[400];
    return $variant === 'pass' ? theme.color.green[500] : theme.color.red[500];
  }};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  font-weight: 600;
  transition: all 0.15s;

  &:hover {
    border-color: ${({ theme, $variant }) =>
      $variant === 'pass' ? theme.color.green[500] : theme.color.red[500]};
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const OfferLetterLlmField = styled.div`
  margin-top: 8px;
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

export const OfferLetterLlmInput = styled.input`
  padding: 6px 8px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 13px;
  color: ${({ theme }) => theme.color.gray[500]};
  background: ${({ theme }) => theme.color.gray[50]};
  width: 100%;

  &:focus {
    outline: none;
    border-color: #009ffb;
    box-shadow: 0 0 0 1px #009ffb;
    background: white;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  &::placeholder {
    color: ${({ theme }) => theme.color.gray[300]};
    font-style: italic;
  }
`;

export const RemoveBonusButton = styled.button`
  cursor: pointer;
  background: none;
  border: none;
  color: #991b1b;
  font-weight: 600;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const AddBonusButton = styled.button`
  cursor: pointer;
  background: none;
  border: none;
  color: #009ffb;
  font-weight: 600;
  font-size: 13px;
  padding: 4px 0;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const OfferLetterStepContainer = styled.div<{
  $reviewed?: boolean;
}>`
  padding: 12px 16px;
  margin-bottom: 8px;
  border-radius: 6px;
  border: 1px solid ${({ theme }) => theme.color.gray[200]};
  border-left: 3px solid
    ${({ $reviewed, theme }) => ($reviewed ? theme.color.gray[200] : '#f59e0b')};
  background: ${({ theme }) => theme.color.white};

  &:last-child {
    margin-bottom: 0;
  }
`;

export const OfferLetterReasonChip = styled.span`
  display: inline-block;
  font-size: 12px;
  font-weight: 500;
  color: #92400e;
  background: #fef3c7;
  padding: 2px 8px;
  border-radius: 12px;
  margin: 2px 4px 2px 0;
`;

export const LeftPanelContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  position: relative;
  display: flex;
  flex-direction: column;
`;

export const RightPanelContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

export const RightPanelReviewContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  max-width: 736px;
`;

export const DocumentHeader = styled.div`
  width: 100%;
  height: 92px;
  min-width: 500px;
  max-width: none;
  gap: 16px;
  border-bottom: 1px solid ${({ theme }) => theme.color.gray[200]};
  padding: 24px;
  background: ${({ theme }) => theme.color.gray[50]};

  @media (max-width: 768px) {
    min-width: 400px;
    padding: 16px;
    height: 80px;
  }
`;

export const DocumentViewer = styled.div<{ $hasModeBar?: boolean }>`
  position: relative;
  width: 100%;
  flex: 1;
  min-width: 500px;
  max-width: none;
  min-height: 400px;
  padding-top: ${({ $hasModeBar }) => ($hasModeBar ? '8px' : '40px')};
  padding-right: 40px;
  padding-left: 40px;
  overflow-y: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({ theme }) => theme.color.gray[50]};

  @media (max-width: 1200px) {
    padding-top: 30px;
    padding-right: 30px;
    padding-left: 30px;
  }

  @media (max-width: 768px) {
    min-width: 400px;
    padding-top: 20px;
    padding-right: 20px;
    padding-left: 20px;
    min-height: 300px;
  }
`;

export const ControlsFooter = styled.div`
  width: 100%;
  height: 153px;
  min-width: 500px;
  max-width: none;
  gap: 16px;
  border-top: 1px solid ${({ theme }) => theme.color.gray[200]};
  padding: 24px;
  background: ${({ theme }) => theme.color.white};
  display: flex;
  align-items: center;
  justify-content: space-between;

  @media (max-width: 1200px) {
    min-width: 500px;
    gap: 12px;
  }

  @media (max-width: 768px) {
    min-width: 400px;
    padding: 16px;
    height: 120px;
    flex-wrap: wrap;
    gap: 8px;
  }
`;

export const AddressInfo = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

export const AddressNameSection = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
`;

export const AddressContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

export const AddressName = styled.div`
  color: ${({ theme }) => theme.color.gray[500]};
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%;
  margin-bottom: 4px;
`;

export const AddressDetails = styled.div`
  color: ${({ theme }) => theme.color.gray[400]};
  font-size: 14px;
  line-height: 145%;
`;

export const PotentialMatchesButton = styled.button`
  position: relative;
  display: flex;
  padding: 8px 12px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 40px;
  border: 1px solid ${({ theme }) => theme.color.gray[500]};
  background: none;
  cursor: pointer;
  font-family: ${({ theme }) => theme.font.normal};
  color: ${({ theme }) => theme.color.gray[500]};
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%;
  align-self: flex-start;
`;

export const NotificationBadge = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  background: ${({ theme }) => theme.color.red[400]};
  color: white;
  border-radius: 50%;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 10px;
  font-weight: 600;
  line-height: 1;
`;

export const SubmissionTooltip = styled.div`
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  margin-bottom: 8px;
  background: ${({ theme }) => theme.color.gray[800]};
  color: white;
  padding: 8px 12px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 500;
  white-space: nowrap;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transition: opacity 0.2s, visibility 0.2s;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    top: 100%;
    left: 50%;
    transform: translateX(-50%);
    border: 4px solid transparent;
    border-top-color: ${({ theme }) => theme.color.gray[800]};
  }
`;

export const SubmissionWithTooltip = styled.div`
  position: relative;

  &:hover ${SubmissionTooltip} {
    opacity: 1;
    visibility: visible;
  }
`;

export const AssignedSubmissionTooltip = styled(SubmissionTooltip)`
  left: 20%;
  transform: translateX(-20%);
`;

export const AssignedSubmissionWithTooltip = styled.div`
  position: relative;

  &:hover ${AssignedSubmissionTooltip} {
    opacity: 1;
    visibility: visible;
  }
`;

export const TopBar = styled.div`
  padding: 24px;
  border-bottom: 1px solid ${({ theme }) => theme.color.gray[150]};
  background-color: ${({ theme }) => theme.color.white};
`;

export const TopBarContent = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
`;

export const ReportPlatformFeedbackLink = styled.button`
  margin: 0 0 0 auto;
  padding: 4px 0;
  border: 0;
  background: none;
  cursor: pointer;
  text-align: center;
  font-family: ${({ theme }) => theme.font.normal.fontFamily};
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  color: ${({ theme }) => theme.color.blue[500]};
  text-decoration: underline;
  text-decoration-thickness: 1px;
  text-underline-offset: 3px;

  &:hover {
    color: ${({ theme }) => theme.color.blue[550]};
  }
`;

export const DropdownContainer = styled.div<{ width: string }>`
  position: relative;
  width: ${({ width }) => width};
`;

export const DropdownLabel = styled.label`
  position: absolute;
  top: -8px;
  left: 12px;
  padding: 0 4px;
  background-color: white;
  color: ${({ theme }) => theme.color.gray[400]};
  font-size: 12px;
  line-height: 16px;
  z-index: 1;
`;

export const DropdownSelect = styled.select<{ width: string }>`
  width: ${({ width }) => width};
  height: 44px;
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.color.gray[250]};
  border-radius: 4px;
  background-color: white;
  appearance: none;
  background-image: url('/assets/images/dropdown.svg');
  background-repeat: no-repeat;
  background-position: right 14px center;
`;

export const DocumentViewerContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: 100%;
  background-color: white;
  border: 1px solid ${({ theme }) => theme.color.gray[150]};
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;

  /* Ensure PDF viewer fills the container */
  > div {
    height: 100%;
    width: 100%;
  }
`;

export const DocumentPlaceholder = styled.div`
  text-align: center;
  color: ${({ theme }) => theme.color.gray[400]};
`;

export const DocumentIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;

export const DocumentTitle = styled.p`
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 8px 0;
`;

export const DocumentSubtitle = styled.p`
  font-size: 14px;
  color: ${({ theme }) => theme.color.gray[350]};
  margin: 8px 0 0 0;
`;

export const BottomBar = styled.div`
  padding: 24px;
  background-color: ${({ theme }) => theme.color.white};
  border-top: 1px solid ${({ theme }) => theme.color.gray[150]};
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  min-height: 132px;

  @media (max-width: 1400px) {
    padding: 20px 24px;
    min-height: 120px;
  }
`;

export const BottomBarContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;

export const DocIdLabel = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.color.gray[750]};
  font-weight: 500;
  white-space: nowrap;
  margin-bottom: 8px;
`;

export const BottomBarControls = styled.div`
  display: flex;
  gap: 16px;
  align-items: center;
  width: 100%;

  @media (max-width: 1400px) {
    gap: 12px;
  }
`;

export const BottomDropdown = styled.select`
  flex: 1;
  height: 44px;
  border: 1px solid ${({ theme }) => theme.color.gray[250]};
  padding: 12px 14px;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;
  appearance: none;
  background-image: url('/assets/images/dropdown.svg');
  background-position: right 8px center;
  background-repeat: no-repeat;
  background-size: 16px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.color.blue[500]};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.color.blue[500]};
  }
`;

export const NavigationButtonsContainer = styled.div`
  display: flex;
  gap: 16px;
  flex-shrink: 0;
`;

export const RailControls = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const RailControlRow = styled.div`
  display: flex;
  gap: 8px;

  & > * {
    flex: 1 1 0;
    min-width: 0;
  }

  [role='button'] {
    padding-right: 12px;
    gap: 8px;
  }

  [role='button'] > div,
  [role='button'] span {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const RailNavigation = styled.div`
  display: flex;
  gap: 8px;
  margin-top: 4px;

  & > button {
    flex: 1 1 0;
    justify-content: center;
  }
`;

export const CustomButton = styled.button`
  min-width: 94px !important;
  height: 44px !important;
  padding: 12px 16px !important;
  border-radius: 4px !important;
  background-color: ${({ theme }) => theme.color.blue[500]} !important;
  border: 1px solid ${({ theme }) => theme.color.blue[500]} !important;
  color: white !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  line-height: 145% !important;
  letter-spacing: 0 !important;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  &:hover {
    background-color: ${({ theme }) => theme.color.blue[550]} !important;
    border-color: ${({ theme }) => theme.color.blue[550]} !important;
  }

  &:disabled {
    background-color: ${({ theme }) => theme.color.gray[150]} !important;
    border-color: ${({ theme }) => theme.color.gray[150]} !important;
    color: ${({ theme }) => theme.color.gray[350]} !important;
    cursor: not-allowed !important;
  }
`;

export const ReviewReportContainer = styled.div`
  margin-top: auto;
  padding-top: 16px;
  display: flex;
  justify-content: center;
`;

export const CustomDropdownContainer = styled.div`
  position: relative;
  flex: 1;
`;

export const CustomDropdownButton = styled.div<{ isOpen: boolean }>`
  padding: 8px 32px 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
`;

export const RailResultPlaceholder = styled(CustomDropdownButton)`
  visibility: hidden;
`;

export const CustomDropdownContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const CustomDropdownIcon = styled.img`
  width: 16px;
  height: 16px;
`;

export const CustomDropdownArrow = styled.img<{ isOpen: boolean }>`
  width: 16px;
  height: 16px;
  transform: ${({ isOpen }) => (isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s;
`;

export const CustomDropdownList = styled.div`
  position: absolute;
  bottom: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid #ccc;
  border-bottom: none;
  border-radius: 4px 4px 0 0;
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
`;

export const CustomDropdownListDown = styled.div`
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: white;
  border: 1px solid #ccc;
  border-top: none;
  border-radius: 0 0 4px 4px;
  z-index: 1000;
  max-height: 200px;
  overflow-y: auto;
`;

export const CustomDropdownOption = styled.div<{ selected: boolean }>`
  margin: 4px;
  padding: 4px 28px 4px 8px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  border-radius: 2px;
  background-color: ${({ theme, selected }) => (selected ? `${theme.color.blue[500]}33` : 'white')};

  &:hover {
    background-color: ${({ theme, selected }) =>
      selected ? `${theme.color.blue[500]}44` : '#f5f5f5'};
  }
`;

export const CustomDropdownOptionCheck = styled.img`
  width: 14px;
  height: 11px;
  margin-left: auto;
`;

export const DocIdContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

export const DocIdSelectWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

export const DocIdIcon = styled.img<{ textLength: number }>`
  position: absolute;
  left: calc(12px + ${({ textLength }) => textLength * 8}px + 8px);
  top: 50%;
  transform: translateY(-50%);
  width: 16px;
  height: 16px;
  pointer-events: none;
`;

export const DocIdSelect = styled(DropdownSelect)`
  padding-right: 40px;
`;

export const DocumentViewerControlsContainer = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;
  display: flex;
  gap: 8px;
  z-index: 10;
`;

export const ViewerModeBar = styled.div`
  display: flex;
  justify-content: center;
  padding: 0 0 8px;
`;

export const ViewerBody = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
`;

export const ViewerModePillContainer = styled.div`
  display: flex;
  gap: 2px;
  padding: 3px;
  border-radius: 999px;
  background-color: ${({ theme }) => theme.color.gray[100]};
  z-index: 10;
  flex-shrink: 0;
`;

export const ViewerModePillButton = styled.button<{ $active: boolean }>`
  border: none;
  border-radius: 999px;
  padding: 6px 20px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  background-color: ${({ $active, theme }) => ($active ? theme.color.gray[900] : 'transparent')};
  color: ${({ $active, theme }) => ($active ? 'white' : theme.color.gray[700])};

  &:hover {
    background-color: ${({ $active, theme }) =>
      $active ? theme.color.gray[900] : theme.color.gray[150]};
  }
`;

export const XrayPanelContainer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  background-color: white;
  border: 1px solid ${({ theme }) => theme.color.gray[150]};
  border-radius: 8px;
  z-index: 5;
  overflow: hidden;
`;

export const XrayTabStrip = styled.div`
  display: flex;
  gap: 4px;
  padding: 16px 16px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.gray[150]};
  overflow-x: auto;
  flex-shrink: 0;
`;

export const XrayTabButton = styled.button<{ $active: boolean }>`
  border: none;
  border-bottom: 2px solid
    ${({ $active, theme }) => ($active ? theme.color.gray[900] : 'transparent')};
  background: none;
  padding: 10px 12px;
  font-size: 13px;
  font-weight: ${({ $active }) => ($active ? 600 : 400)};
  color: ${({ $active, theme }) => ($active ? theme.color.gray[900] : theme.color.gray[600])};
  cursor: pointer;
  white-space: nowrap;
`;

export const XrayTabContent = styled.div`
  flex: 1;
  overflow: auto;
  padding: 0 16px 16px;
  font-size: 13px;

  > :first-child {
    margin-top: 16px;
  }

  table {
    width: 100%;
    max-width: 100%;
    border-collapse: collapse;
    margin-bottom: 16px;
  }

  th,
  td {
    border: 1px solid ${({ theme }) => theme.color.gray[150]};
    padding: 4px 8px;
    text-align: left;
    font-size: 12px;
    vertical-align: top;
  }

  td.long {
    overflow-wrap: anywhere;
  }

  th {
    padding: 4px 6px;
  }

  thead tr:last-child th {
    position: sticky;
    top: 0;
    background: ${({ theme }) => theme.color.white};
    box-shadow: inset 0 -1px 0 ${({ theme }) => theme.color.gray[150]};
  }

  pre {
    white-space: pre-wrap;
    word-break: break-word;
    font-size: 12px;
    margin: 0;
  }
`;

export const XrayRawText = styled.td`
  white-space: break-spaces;
  overflow-wrap: anywhere;
  font-family: monospace;
`;

export const XraySectionHeading = styled.h4`
  margin: 20px 0 8px;
  font-size: 16px;
  font-weight: 600;

  &:first-of-type {
    margin-top: 8px;
  }
`;

export const XrayCenteredState = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background-color: white;
  z-index: 5;
  border-radius: 8px;
`;

export const ControlButton = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 4px;
  border: 1px solid ${({ theme }) => theme.color.gray[150]};
  background-color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background-color: #f9fafb;
    border-color: ${({ theme }) => theme.color.gray[250]};
  }

  &:active {
    background-color: ${({ theme }) => theme.color.gray[100]};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  img {
    width: 16px;
    height: 16px;
  }
`;

export const DocumentViewerWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
`;

export const PDFViewerContainer = styled.div`
  height: 100%;
  width: 100%;
`;

export const DropdownSelectWrapper = styled.div<{ hasError?: boolean }>`
  position: relative;
  width: 362px;
  border: ${({ theme, hasError }) =>
    hasError ? `1px solid ${theme.color.red[500]}` : '1px solid transparent'};
  border-radius: 4px;

  & > div {
    border: ${({ theme, hasError }) =>
      hasError ? `1px solid ${theme.color.red[500]}` : `1px solid ${theme.color.gray[250]}`};
    border-radius: 4px;
  }
`;

export const SubmissionDropdownWrapper = styled.div`
  position: relative;
  width: 180px;
`;

export const WarningBanner = styled.div`
  position: absolute;
  top: 8px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  width: 860px;
  height: 40px;
  padding: 12px 20px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.color.yellow[700]};
  background: #f8ce75;
  color: ${({ theme }) => theme.color.yellow[800]};
  font-size: 14px;
  font-weight: 500;
  line-height: 1.4;
  z-index: 10;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

// Submission Review Styles
export const SubmissionReviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow-y: auto;
  padding: 64px;
  max-width: 736px;
`;

export const SubmissionReviewHeader = styled.div`
  margin-bottom: 32px;
`;

export const SubmissionReviewDescription = styled.p`
  padding-top: 17px;
  color: ${({ theme }) => theme.color.gray[500]};
  font-size: 16px;
  font-style: normal;
  font-weight: 500;
  line-height: 145%;
  margin: 0;
  font-family: ${({ theme }) => theme.font.normal};
`;

export const SubmissionReviewTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const SubmissionReviewTitle = styled.h1`
  color: ${({ theme }) => theme.color.gray[500]};
  font-family: ${({ theme }) => theme.font.normal};
  font-size: 24px;
  font-style: normal;
  font-weight: 600;
  margin: 0;
`;

export const SubmissionReviewContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 32px;
  flex: 1;
`;

export const EntryDropdownContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const EntryDropdownLabel = styled.label`
  font-size: 14px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.gray[750]};
`;

export const DocumentsList = styled.div`
  margin-top: 24px;

  h3 {
    font-size: 16px;
    font-weight: 600;
    color: ${({ theme }) => theme.color.gray[800]};
    margin: 0 0 16px 0;
  }
`;

export const DocumentItem = styled.div`
  padding: 12px 0;
  border-bottom: 1px solid ${({ theme }) => theme.color.gray[150]};
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: relative;

  &:last-child {
    border-bottom: none;
  }
`;

export const DocumentInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex: 1;

  > span:first-child {
    font-size: 14px;
    font-weight: 500;
    color: ${({ theme }) => theme.color.gray[800]};
    text-decoration: underline;
    cursor: pointer;

    &:hover {
      color: ${({ theme }) => theme.color.blue[600]};
    }
  }
`;

export const DocumentRuling = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  img {
    width: 16px;
    height: 16px;
  }

  span {
    font-size: 14px;
    color: ${({ theme }) => theme.color.gray[750]};
    font-weight: 500;
  }
`;

export const DocumentStatusIcon = styled.img`
  width: 16px;
  height: 16px;
`;

export const DocumentStatusText = styled.span`
  color: ${({ theme }) => theme.color.gray[500]};
  font-size: 14px;
  font-style: italic;
  font-weight: 500;
  line-height: 115%;
`;

export const DocumentStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const DocumentEditButton = styled.button`
  background: none;
  border: none;
  color: ${({ theme }) => theme.color.blue[600]};
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  padding: 4px 8px;
  border-radius: 4px;
  margin-right: 100px;

  &:hover {
    background: ${({ theme }) => theme.color.blue[75]};
  }
`;

export const SendReportContainer = styled.div``;

export const SendReportButton = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  background: ${({ theme, disabled }) =>
    disabled ? theme.color.gray[150] : theme.color.blue[500]};
  color: ${({ theme, disabled }) => (disabled ? theme.color.gray[350] : 'white')};
  border: none;
  border-radius: 4px;
  padding: 12px 24px;
  font-size: 14px;
  font-weight: 600;
  cursor: ${({ disabled }) => (disabled ? 'not-allowed' : 'pointer')};

  &:hover {
    background: ${({ theme, disabled }) =>
      disabled ? theme.color.gray[150] : theme.color.blue[550]};
  }

  img {
    width: 20px;
    height: 20px;
  }
`;

export const ValidationMessage = styled.div`
  color: ${({ theme }) => theme.color.red[500]};
  font-size: 12px;
  margin-top: 4px;
`;

export const ErrorValidationMessage = styled(ValidationMessage)`
  margin-top: 8px;
  color: red;
`;

export const PreviewContainer = styled.div<{ isVisible: boolean; x: number; y: number }>`
  position: fixed;
  top: 50%;
  left: 650px;
  transform: translateY(-50%);
  width: 600px;
  height: 700px;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.12);
  z-index: 9999;
  display: ${(props) => (props.isVisible ? 'flex' : 'none')};
  pointer-events: none;
  overflow: hidden;
  padding: 20px;
  flex-direction: column;
  align-items: flex-start;
  gap: 12px;
`;

export const PreviewHeader = styled.div`
  width: 100%;
  text-align: left;
  flex-shrink: 0;
  position: relative;
  z-index: 10;
  height: 20px;
  display: flex;
  align-items: center;
`;

export const DocumentId = styled.div`
  color: ${({ theme }) => theme.color.gray[500]};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 115%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ProofPreviewContainer = styled.div`
  position: relative;
  background: #f5f5f5;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;

  img {
    max-width: 100%;
    height: auto;
    display: block;
    border-radius: 4px;
  }
`;

export const PreviewFooter = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 8px;
  flex-shrink: 0;
  position: relative;
  z-index: 10;
  height: 24px;
`;

export const RulingIcon = styled.img`
  width: 16px;
  height: 16px;
`;

export const RulingText = styled.span<{ status: string }>`
  color: ${({ theme }) => theme.color.gray[500]};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 115%;
`;

export const LoadingContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #666;
  font-size: 12px;
`;

export const PreviewImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

export const NoThumbnailMessage = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: ${({ theme }) => theme.color.gray[400]};
  font-size: 14px;
  text-align: center;
`;

export const ViewerContent = styled.div`
  background-color: ${({ theme }) => theme.color.white};
  margin: 4rem;
  max-width: 1024px;
`;

// Modern Submission Dropdown Styles
export const ModernDropdownContainer = styled.div`
  position: relative;
  display: inline-block;
`;

export const ModernDropdownTrigger = styled.div<{ $isOpen: boolean; $disabled: boolean }>`
  padding: 8px 32px 8px 12px;
  border: 1px solid #ccc;
  border-radius: 4px;
  background-color: white;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  opacity: ${({ $disabled }) => ($disabled ? 0.6 : 1)};
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: relative;
  min-height: 40px;
`;

export const ModernDropdownContent = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  min-width: 0;
`;

export const ModernSelectedOptionContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

export const ModernStatusIcon = styled.img`
  width: 16px;
  height: 16px;
  flex-shrink: 0;
`;

export const ModernStatusDot = styled.div<{ $status: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex-shrink: 0;
  background-color: ${({ theme, $status }) => {
    switch ($status) {
      case 'CLEAN':
        return theme.color.green[400];
      case 'EDITED':
        return theme.color.yellow[700];
      case 'UNDETERMINED':
        return theme.color.red[400];
      case 'PENDING':
      default:
        return theme.color.gray[400];
    }
  }};
`;

export const ModernSubmissionText = styled.span`
  color: ${({ theme }) => theme.color.gray[500]};
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 115%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
  margin-bottom: 2px;
`;

export const ModernPlaceholderText = styled.span`
  color: ${({ theme }) => theme.color.gray[500]};
  font-size: 14px;
  font-style: normal;
  font-weight: 600;
  line-height: 145%;
`;

export const ModernChevronIcon = styled.div<{ $isOpen: boolean }>`
  width: 16px;
  height: 16px;
  transform: ${({ $isOpen }) => ($isOpen ? 'rotate(180deg)' : 'rotate(0deg)')};
  transition: transform 0.2s;
  position: absolute;
  right: 12px;
  top: 50%;
  margin-top: -8px;
`;

export const ModernDropdownMenu = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  width: 620px;
  background: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.gray[200]};
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  z-index: 1000;
  overflow: hidden;
  max-height: 420px;
  overflow-y: auto;
`;

export const ModernDropdownHeader = styled.div`
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.color.gray[200]};
  background: ${({ theme }) => theme.color.gray[50]};
`;

export const ModernHeaderText = styled.div`
  color: ${({ theme }) => theme.color.gray[700]};
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 4px;
`;

export const ModernSubmissionCount = styled.div`
  color: ${({ theme }) => theme.color.gray[500]};
  font-size: 12px;
  font-weight: 400;
`;

export const ModernOptionsList = styled.div`
  padding: 8px;
`;

export const ModernDropdownOption = styled.div<{
  $selected: boolean;
  $isFirst: boolean;
  $isLast: boolean;
}>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px;
  margin-bottom: 4px;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  background: ${({ theme }) => theme.color.white};
  border: 1px solid ${({ theme }) => theme.color.gray[200]};
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:last-child {
    margin-bottom: 0;
  }

  &:hover {
    background: ${({ theme }) => theme.color.gray[50]};
    border-color: ${({ theme }) => theme.color.gray[300]};
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.color.blue[600]};
    box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.2);
  }

  ${({ $selected, theme }) =>
    $selected &&
    `
    background: ${theme.color.blue[50]};
    border-color: ${theme.color.blue[600]};
    box-shadow: 0 2px 6px rgba(37, 99, 235, 0.2);
  `}
`;

export const ModernOptionContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
  flex: 1;
`;

export const ModernOptionText = styled.span`
  color: ${({ theme }) => theme.color.gray[500]};
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 115%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  display: block;
`;

export const ModernCheckIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${({ theme }) => theme.color.blue[600]};
  flex-shrink: 0;
`;

// New components for the sectioned dropdown
export const ModernDropdownSection = styled.div`
  padding: 12px;
`;

export const ModernSectionTitle = styled.div`
  color: ${({ theme }) => theme.color.gray[600]};
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.5px;
  margin-bottom: 8px;
`;

export const ModernAssignedSubmission = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  background: ${({ theme }) => theme.color.white};
  border: 2px solid ${({ $selected, theme }) => ($selected ? theme.color.blue[600] : 'transparent')};
  border-radius: 6px;
  box-shadow: ${({ $selected }) => ($selected ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none')};
  max-width: 280px;

  &:hover {
    background: ${({ $selected, theme }) =>
      $selected ? theme.color.blue[50] : theme.color.gray[50]};
    border-color: ${({ $selected, theme }) =>
      $selected ? theme.color.blue[700] : theme.color.gray[300]};
    box-shadow: ${({ $selected }) =>
      $selected ? '0 2px 6px rgba(37, 99, 235, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.1)'};
  }

  ${({ $selected, theme }) =>
    $selected &&
    `
    background: ${theme.color.blue[100]};
    border-color: ${theme.color.blue[700]};
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.3);
  `}
`;

export const ModernOtherSubmissionsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 8px;
`;

export const ModernOtherSubmission = styled.div<{ $selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  cursor: pointer;
  transition: all 0.15s ease-in-out;
  background: ${({ theme }) => theme.color.white};
  border: 2px solid ${({ $selected, theme }) => ($selected ? theme.color.blue[600] : 'transparent')};
  border-radius: 6px;
  box-shadow: ${({ $selected }) => ($selected ? '0 1px 3px rgba(0, 0, 0, 0.1)' : 'none')};

  &:hover {
    background: ${({ $selected, theme }) =>
      $selected ? theme.color.blue[50] : theme.color.gray[50]};
    border-color: ${({ $selected, theme }) =>
      $selected ? theme.color.blue[700] : theme.color.gray[300]};
    box-shadow: ${({ $selected }) =>
      $selected ? '0 2px 6px rgba(37, 99, 235, 0.15)' : '0 1px 3px rgba(0, 0, 0, 0.1)'};
  }

  ${({ $selected, theme }) =>
    $selected &&
    `
    background: ${theme.color.blue[100]};
    border-color: ${theme.color.blue[700]};
    box-shadow: 0 2px 8px rgba(37, 99, 235, 0.3);
  `}
`;

export const ListChecksPanelContainer = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  padding-bottom: 24px;
  background-color: ${({ theme }) => theme.color.gray[50]};
  z-index: 5;
`;

export const ListChecksLayout = styled.div`
  display: flex;
  flex: 1;
  min-height: 0;
  background-color: white;
  border: 1px solid ${({ theme }) => theme.color.gray[150]};
  border-radius: 8px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

export const ListChecksMaster = styled.div`
  flex: 0 0 318px;
  width: 318px;
  overflow-y: auto;
  padding: 10px 0 14px;
  border-right: 1px solid ${({ theme }) => theme.color.gray[150]};
  background-color: ${({ theme }) => theme.color.gray[50]};

  @media (max-width: 1320px) {
    flex-basis: 282px;
    width: 282px;
  }
`;

export const ListCheckGroup = styled.div`
  display: contents;
`;

export const ListCheckGroupLabel = styled.p<{ $needs?: boolean }>`
  margin: 0;
  padding: 12px 16px 6px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${({ $needs, theme }) => ($needs ? theme.color.gray[800] : theme.color.gray[350])};
`;

export const ListCheckRowName = styled.span`
  font-size: 13px;
  font-weight: 500;
  line-height: 1.35;
  white-space: nowrap;
  color: ${({ theme }) => theme.color.gray[750]};
`;

export const ListCheckRow = styled.button<{ $active: boolean; $quiet: boolean }>`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 9px 16px;
  border: none;
  border-left: 3px solid
    ${({ $active, theme }) => ($active ? theme.color.blue[500] : 'transparent')};
  background: ${({ $active, theme }) => ($active ? theme.color.blue[80] : 'none')};
  text-align: left;
  cursor: pointer;

  &:hover {
    background-color: ${({ $active, theme }) =>
      $active ? theme.color.blue[80] : theme.color.gray[100]};
  }

  ${ListCheckRowName} {
    font-weight: ${({ $active, $quiet }) => ($active ? ($quiet ? 500 : 600) : $quiet ? 400 : 500)};
    color: ${({ $active, $quiet, theme }) =>
      $active ? theme.color.gray[900] : $quiet ? theme.color.gray[500] : theme.color.gray[750]};
  }
`;

export const ListCheckChipSpan = styled.span<{ $tone: ChipTone }>`
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${({ $tone, theme }) =>
    $tone === 'neutral' ? theme.color.gray[400] : theme.color.gray[750]};

  svg {
    flex: 0 0 auto;
    display: block;
    color: ${({ $tone, theme }) =>
      $tone === 'good'
        ? theme.color.green[800]
        : $tone === 'bad'
        ? theme.color.red[500]
        : $tone === 'open'
        ? theme.color.yellow[700]
        : theme.color.gray[350]};
  }

  ${ListCheckRow} & {
    font-size: 9.5px;
  }
`;

export const ListChecksDetail = styled.div`
  flex: 1;
  min-width: 0;
  min-height: 0;
  display: flex;
  flex-direction: column;
`;

export const ListCheckDetailHead = styled.div`
  flex-shrink: 0;
  padding: 13px 20px 12px;
  border-bottom: 1px solid ${({ theme }) => theme.color.gray[150]};
`;

export const ListCheckTitleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

export const ListCheckTitle = styled.h3`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.gray[900]};
`;

export const ListCheckLede = styled.p`
  margin: 6px 0 0;
  max-width: 70ch;
  font-size: 13px;
  line-height: 1.55;
  color: ${({ theme }) => theme.color.gray[400]};
`;

export const ListCheckLedeRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
  margin-top: 6px;

  & > ${ListCheckLede} {
    margin: 0;
  }
`;

export const ListCheckLedeValue = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.color.gray[750]};
`;

export const ListCheckAnswered = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin: 0 0 12px;
  font-size: 13px;
  font-weight: 500;
  color: ${({ theme }) => theme.color.green[800]};
`;

export const ListCheckExternalLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  flex: 0 0 auto;
  margin-left: auto;
  font-size: 13px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.blue[500]};
  text-decoration: underline;
`;

export const ListCheckDetailBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 16px 20px;
`;

export const ListCheckMatchedCard = styled.div`
  padding: 13px 15px;
  border: 1px solid ${({ theme }) => theme.color.gray[200]};
  border-radius: 6px;
  background-color: ${({ theme }) => theme.color.gray[50]};
`;

export const ListCheckMatchedLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  line-height: 1.4;
  color: ${({ theme }) => theme.color.gray[800]};
`;

export const ListCheckMatchedRef = styled.div`
  margin-top: 3px;
  font-size: 12.5px;
  color: ${({ theme }) => theme.color.gray[400]};
`;

export const ListCheckCandidateSub = styled.p`
  margin: 2px 0 0;
  font-size: 11.5px;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.color.gray[400]};
`;

export const ListCheckNote = styled.div`
  margin-top: 10px;
  padding: 9px 11px 9px 12px;
  border: 1px solid ${({ theme }) => theme.color.gray[200]};
  border-left: 3px solid ${({ theme }) => theme.color.yellow[700]};
  border-radius: 0 4px 4px 0;
  background-color: white;
  font-size: 12.5px;
  line-height: 1.5;
  white-space: pre-wrap;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.color.gray[750]};
`;

export const ListCheckNoteLabel = styled.span`
  display: block;
  margin-bottom: 3px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.yellow[800]};
`;

export const ListCheckException = styled(ListCheckNote)`
  border-left-color: ${({ theme }) => theme.color.purple[700]};
  background-color: ${({ theme }) => theme.color.purple[100]};

  ${ListCheckNoteLabel} {
    color: ${({ theme }) => theme.color.purple[700]};
  }
`;

export const ListCheckNoteMeta = styled.span`
  display: block;
  margin-bottom: 3px;
  font-size: 11px;
  color: ${({ theme }) => theme.color.gray[400]};
`;

export const ListCheckRowNote = styled.div`
  & + & {
    margin-top: 8px;
  }
`;

export const ListCheckEmptyState = styled.p`
  display: flex;
  align-items: center;
  gap: 9px;
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.color.gray[400]};
`;

export const ListChecksCenteredState = styled.div`
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 12px;
  background-color: white;
  z-index: 5;
  border-radius: 8px;
`;

export const ListCheckDetailFoot = styled.div`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 20px;
  border-top: 1px solid ${({ theme }) => theme.color.gray[150]};
  background-color: white;
`;

export const ListCheckFootSpacer = styled.span`
  flex: 1;
`;

export const CustomOutlineButton = styled(CustomButton)`
  background-color: white !important;
  border-color: ${({ theme }) => theme.color.gray[250]} !important;
  color: ${({ theme }) => theme.color.gray[750]} !important;

  &:hover:not(:disabled) {
    background-color: ${({ theme }) => theme.color.gray[50]} !important;
    border-color: ${({ theme }) => theme.color.gray[300]} !important;
  }
`;

export const ListCheckSelectButton = styled(CustomButton)`
  justify-content: center;
  min-width: 0 !important;
  height: 30px !important;
  padding: 0 11px !important;
  font-size: 12px !important;
`;

export const ListCheckNavButton = styled(CustomOutlineButton)`
  justify-content: center;
  gap: 6px;
  min-width: 0 !important;
  height: 30px !important;
  padding: 0 12px !important;
  font-size: 12px !important;
  color: ${({ theme }) => theme.color.gray[500]} !important;

  &:disabled img {
    opacity: 0.4;
  }
`;

export const ListCheckSecondaryButton = styled(CustomOutlineButton)`
  justify-content: center;
  min-width: 0 !important;
  height: 36px !important;
  padding: 0 14px !important;
  font-size: 13px !important;
  color: ${({ theme }) => theme.color.gray[500]} !important;
`;

export const ListCheckStackCount = styled.span`
  min-width: 62px;
  text-align: center;
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.gray[400]};
`;

export const ListCheckTile = styled.div<{ $pick: boolean }>`
  position: relative;
  display: flex;
  flex-direction: column;
  padding: 12px 14px;
  border: 1px solid ${({ $pick, theme }) => ($pick ? theme.color.blue[100] : theme.color.gray[200])};
  border-radius: 6px;
  background-color: ${({ $pick, theme }) => ($pick ? theme.color.blue[70] : 'white')};
  box-shadow: ${({ $pick, theme }) => ($pick ? `inset 3px 0 0 ${theme.color.blue[500]}` : 'none')};

  &:hover {
    border-color: ${({ $pick, theme }) => ($pick ? theme.color.blue[100] : theme.color.gray[250])};
  }
`;

export const ListCheckTileTop = styled.div`
  display: flex;
  align-items: baseline;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 2px;
`;

export const ListCheckTileSub = styled.span`
  font-size: 11.5px;
  font-weight: 400;
  letter-spacing: normal;
  text-transform: none;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.color.gray[400]};
`;

export const ListCheckRowLink = styled.a`
  margin-right: 5px;
  color: ${({ theme }) => theme.color.blue[500]};
  text-decoration: underline;
`;

export const ListCheckRowRef = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  color: inherit;
  cursor: pointer;

  &:hover,
  &:focus-visible {
    color: ${({ theme }) => theme.color.gray[700]};
  }
`;

export const ListCheckPickTag = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 2px 7px;
  border-radius: 999px;
  background-color: ${({ theme }) => theme.color.blue[100]};
  font-size: 9.5px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  white-space: nowrap;
  color: ${({ theme }) => theme.color.blue[800]};
`;

export const ListCheckPanes = styled.div`
  display: flex;
  gap: 16px;
  height: 36vh;
  min-height: 250px;
  margin: 4px 0 14px;
`;

export const ListCheckPane = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
`;

export const ListCheckPaneLabel = styled.p`
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin: 0 0 7px;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.color.gray[400]};
`;

export const ListCheckPaneDownload = styled.button`
  margin-left: auto;
  padding: 0;
  border: 0;
  background: none;
  font: inherit;
  letter-spacing: inherit;
  text-transform: inherit;
  color: ${({ theme }) => theme.color.blue[500]};
  cursor: pointer;

  &:hover,
  &:focus-visible {
    text-decoration: underline;
  }
`;

export const ListCheckPaneFrame = styled.div<{ $empty: boolean }>`
  position: relative;
  flex: 1;
  min-height: 0;
  display: flex;
  border: 1px ${({ $empty }) => ($empty ? 'dashed' : 'solid')}
    ${({ theme }) => theme.color.gray[200]};
  border-radius: 4px;
  background-color: white;
  overflow-x: hidden;
  overflow-y: auto;
`;

export const ListCheckPaneViewer = styled.div`
  flex: 1;
  min-height: 0;
`;

export const ListCheckPaneStatus = styled.p`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding: 0 12px;
  text-align: center;
  font-size: 12.5px;
  color: ${({ theme }) => theme.color.gray[350]};
  pointer-events: none;
`;

export const ListCheckCompare = styled.div`
  border: 1px solid ${({ theme }) => theme.color.gray[150]};
  border-radius: 6px;
  overflow: hidden;
`;

export const ListCheckCompareHead = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background-color: ${({ theme }) => theme.color.gray[50]};
  border-bottom: 1px solid ${({ theme }) => theme.color.gray[150]};
`;

export const ListCheckCompareTitle = styled.span`
  font-size: 12px;
  font-weight: 700;
  color: ${({ theme }) => theme.color.gray[800]};
`;

export const ListCheckCompareSummary = styled.span`
  margin-right: auto;
  font-size: 12px;
  color: ${({ theme }) => theme.color.gray[400]};
`;

export const ListCheckCompareMatchPercent = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #166534;
`;

type CompareTone = 'amber' | 'red' | 'green' | 'plain';

const ROW_TONES: Record<CompareTone, { background: string; edge: string }> = {
  amber: { background: '#fffcf5', edge: '#f0b65a' },
  red: { background: '#fff8f7', edge: '#f5a39b' },
  green: { background: '#f5fbf7', edge: '#86c99b' },
  plain: { background: 'white', edge: 'transparent' }
};

export const ListCheckCompareRow = styled.div<{ $tone?: CompareTone; $head?: boolean }>`
  display: grid;
  grid-template-columns: minmax(130px, 0.7fr) 1fr 1fr 110px;
  gap: 12px;
  align-items: start;
  padding: ${({ $head }) => ($head ? '6px 12px' : '7px 12px')};
  border-top: ${({ $head }) => ($head ? 'none' : '1px solid #f1f2f4')};
  background-color: ${({ $tone = 'plain' }) => ROW_TONES[$tone].background};
  box-shadow: inset 3px 0 0 ${({ $tone = 'plain' }) => ROW_TONES[$tone].edge};
  font-size: ${({ $head }) => ($head ? '10.5px' : '13px')};
  font-weight: ${({ $head }) => ($head ? 700 : 400)};
  letter-spacing: ${({ $head }) => ($head ? '0.06em' : 'normal')};
  text-transform: ${({ $head }) => ($head ? 'uppercase' : 'none')};
  color: ${({ $head, theme }) => ($head ? theme.color.gray[400] : theme.color.gray[750])};
`;

export const ListCheckCompareColumn = styled.span`
  font-weight: 500;
  color: ${({ theme }) => theme.color.gray[750]};
`;

export const ListCheckCompareValue = styled.span<{ $list?: boolean }>`
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  min-width: 0;
  overflow-wrap: anywhere;
  font-weight: ${({ $list }) => ($list ? 600 : 400)};
  color: ${({ $list, theme }) => ($list ? theme.color.gray[900] : theme.color.gray[750])};
`;

export const ListCheckCompareMuted = styled.span`
  display: block;
  width: 100%;
  font-style: italic;
  color: ${({ theme }) => theme.color.gray[400]};
`;

export const ListCheckCompareClosest = styled.span`
  display: block;
  width: 100%;
  font-size: 11.5px;
  color: ${({ theme }) => theme.color.gray[350]};
`;

export const ListCheckCompareRule = styled.code`
  padding: 1px 5px;
  border-radius: 3px;
  background-color: ${({ theme }) => theme.color.gray[100]};
  font-family: 'Roboto Mono', ui-monospace, monospace;
  font-size: 12px;
`;

type CompareChipTone = 'shared' | 'document' | 'list';

const CHIP_TONES: Record<CompareChipTone, { background: string; color: string }> = {
  shared: { background: '#eef1f5', color: '#4a5568' },
  document: { background: '#fdf0d5', color: '#92400e' },
  list: { background: '#e3eefd', color: '#1e40af' }
};

export const ListCheckCompareChip = styled.span<{ $tone: CompareChipTone }>`
  padding: 1px 7px;
  border-radius: 3px;
  font-size: 11.5px;
  font-weight: 500;
  background-color: ${({ $tone }) => CHIP_TONES[$tone].background};
  color: ${({ $tone }) => CHIP_TONES[$tone].color};
`;

const PILL_TONES: Record<CompareTone, { background: string; color: string }> = {
  amber: { background: '#fdf0d5', color: '#92400e' },
  red: { background: '#fdecea', color: '#b42318' },
  green: { background: '#e8f5ec', color: '#166534' },
  plain: { background: '#eef1f5', color: '#4a5568' }
};

export const ListCheckComparePill = styled.span<{ $tone: CompareTone }>`
  justify-self: end;
  padding: 3px 9px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  white-space: nowrap;
  background-color: ${({ $tone }) => PILL_TONES[$tone].background};
  color: ${({ $tone }) => PILL_TONES[$tone].color};
`;

export const ListCheckCompareMark = styled.mark<{ $tone: CompareTone }>`
  padding: 0 2px;
  border-radius: 2px;
  font-weight: 600;
  background-color: ${({ $tone }) => ($tone === 'green' ? '#ddf1e4' : '#fde2df')};
  color: ${({ $tone }) => ($tone === 'green' ? '#166534' : '#b42318')};
`;

export const ListCheckCompareSame = styled.p`
  margin: 8px 0 0;
  font-size: 12px;
  color: ${({ theme }) => theme.color.gray[400]};
`;

export const ListCheckModalList = styled.ul`
  margin: 10px 0;
  padding-left: 18px;

  li {
    margin-bottom: 3px;
    font-size: 13px;
    line-height: 1.55;
    color: ${({ theme }) => theme.color.gray[750]};
  }
`;

export const ListCheckAnswerNoteLabel = styled.label`
  display: block;
  margin-top: 12px;
  font-size: 13px;
  color: ${({ theme }) => theme.color.gray[600]};

  textarea {
    margin-top: 6px;
  }
`;

export const ListCheckRegistry = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

export const ListCheckRegistryFacts = styled.dl`
  display: grid;
  grid-template-columns: max-content 1fr;
  gap: 6px 16px;
  margin: 0;
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.color.gray[200]};
  border-radius: 6px;

  dt {
    font-size: 13px;
    color: ${({ theme }) => theme.color.gray[400]};
  }

  dd {
    margin: 0;
    font-size: 14px;
    font-weight: 600;
    color: ${({ theme }) => theme.color.gray[900]};
  }
`;

export const ListCheckRegistryDocument = styled.div`
  display: flex;
  height: 52vh;
  min-height: 300px;
`;

export const ListCheckTabStrip = styled.div`
  flex-shrink: 0;
  display: flex;
  align-self: flex-start;
  gap: 2px;
  margin: 12px 20px 0;
  padding: 3px;
  border-radius: 999px;
  background-color: ${({ theme }) => theme.color.gray[100]};
`;

export const ListCheckTabButton = styled.button<{ $active: boolean }>`
  border: none;
  border-radius: 999px;
  padding: 5px 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background-color: ${({ $active, theme }) => ($active ? theme.color.gray[900] : 'transparent')};
  color: ${({ $active, theme }) => ($active ? 'white' : theme.color.gray[700])};

  &:hover {
    background-color: ${({ $active, theme }) =>
      $active ? theme.color.gray[900] : theme.color.gray[150]};
  }
`;

export const TextCompareScroll = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
`;

export const TextCompareTable = styled.table`
  width: 100%;
  table-layout: fixed;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 12px;
  font-variant-numeric: tabular-nums;

  th,
  td {
    padding: 5px 6px;
    text-align: left;
    vertical-align: top;
    overflow-wrap: anywhere;
    border-right: 1px solid ${({ theme }) => theme.color.gray[150]};
    border-bottom: 1px solid ${({ theme }) => theme.color.gray[150]};
    color: ${({ theme }) => theme.color.gray[800]};
  }

  th:last-child,
  td:last-child {
    border-right: none;
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  thead th {
    position: sticky;
    top: 0;
    z-index: 1;
    box-sizing: border-box;
    height: 26px;
    padding-top: 0;
    padding-bottom: 0;
    vertical-align: middle;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    background-color: white;
    font-weight: 600;
    box-shadow: 0 1px 0 ${({ theme }) => theme.color.gray[250]}, 0 3px 4px -2px rgba(0, 0, 0, 0.12);
  }
`;

export const TextComparePageCell = styled.td`
  font-weight: 600;
  background-color: ${({ theme }) => theme.color.gray[50]};
`;

export const TextCompareRef = styled.span`
  margin-left: 6px;
  font-weight: 500;
  letter-spacing: 0;
  text-transform: none;
  color: ${({ theme }) => theme.color.blue[500]};
`;

export const TextCompareSwatch = styled.span<{ $color: string }>`
  display: inline-block;
  width: 10px;
  height: 10px;
  margin-right: 6px;
  vertical-align: -1px;
  border-radius: 2px;
  border: 1px solid rgba(0, 0, 0, 0.15);
  background-color: ${({ $color }) => $color};
`;

export const ListCheckCompareGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: 14px;
  height: 100%;
  min-height: 320px;
`;

export const ListCheckCompareSide = styled.section`
  display: flex;
  flex-direction: column;
  min-height: 0;
  border: 1px solid ${({ theme }) => theme.color.gray[150]};

  h4 {
    margin: 0;
    padding: 8px 6px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    color: ${({ theme }) => theme.color.gray[500]};
    border-bottom: 1px solid ${({ theme }) => theme.color.gray[150]};
  }

  p {
    margin: 0;
    padding: 6px;
    font-size: 12px;
    color: ${({ theme }) => theme.color.gray[400]};
  }

  pre {
    flex: 1;
    min-height: 0;
    margin: 0;
    padding: 6px;
    overflow: auto;
    font-size: 11px;
    line-height: 1.45;
    white-space: pre-wrap;
    overflow-wrap: anywhere;
    color: ${({ theme }) => theme.color.gray[800]};
    background-color: ${({ theme }) => theme.color.gray[50]};
  }
`;

export const FontCheckHowTo = styled.p`
  margin: 0 0 16px;
  max-width: 760px;
  font-size: 13px;
  color: ${({ theme }) => theme.color.gray[750]};
`;

export const FontCheckLabel = styled.div<{ $bad?: boolean }>`
  margin-top: 10px;
  font-size: 10.5px;
  font-weight: 700;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  color: ${({ $bad, theme }) => ($bad ? theme.color.red[500] : theme.color.gray[750])};

  &:first-child {
    margin-top: 0;
  }
`;

export const FontDescriptorList = styled.div`
  display: grid;
  gap: 8px;
  max-width: 760px;
`;

export const FontDescriptorCard = styled.div<{ $moved: boolean }>`
  padding: 8px 12px;
  border: 1px solid ${({ $moved, theme }) => ($moved ? '#fca5a5' : theme.color.gray[200])};
  border-radius: 6px;
  background-color: #fff;
`;

export const FontDescriptorName = styled.div<{ $moved: boolean }>`
  margin-bottom: 4px;
  font-size: 12px;
  font-weight: 600;
  color: ${({ $moved, theme }) => ($moved ? theme.color.red[500] : theme.color.gray[900])};
`;

export const FontDescriptorText = styled.div`
  font-family: monospace;
  font-size: 11.5px;
  line-height: 1.7;
  word-break: break-all;
  user-select: text;
  color: ${({ theme }) => theme.color.gray[750]};
`;

export const FontDescriptorKey = styled.span<{ $moved: boolean }>`
  font-weight: 600;
  color: ${({ $moved, theme }) => ($moved ? theme.color.red[500] : theme.color.gray[900])};
  ${({ $moved, theme }) =>
    $moved &&
    `
    padding: 0 1px;
    border-radius: 2px;
    outline: 1.5px solid ${theme.color.red[500]};
    background-color: ${theme.color.red[100]};
  `}
`;

export const HeaderBarBox = styled.div`
  display: grid;
  gap: 6px;
  max-width: 760px;
  padding: 14px 16px 16px;
  overflow-x: auto;
  border: 1px solid ${({ theme }) => theme.color.gray[200]};
  border-radius: 8px;
  background-color: #fff;
`;

export const HeaderBarExpected = styled.div`
  padding: 8px 12px;
  border: 1px solid ${({ theme }) => theme.color.gray[150]};
  border-radius: 6px;
  background-color: ${({ theme }) => theme.color.gray[50]};
`;

export const HeaderBarRow = styled.div`
  display: grid;
  grid-template-columns: 56px minmax(0, 1fr);
  gap: 10px;
  align-items: center;
`;

export const HeaderBarPage = styled.span`
  font-size: 12px;
  color: ${({ theme }) => theme.color.gray[400]};
`;

export const HeaderBarText = styled.span<{ $muted?: boolean }>`
  font-family: monospace;
  font-size: 11px;
  white-space: pre-wrap;
  user-select: text;
  cursor: text;
  color: ${({ $muted, theme }) => ($muted ? theme.color.gray[500] : theme.color.gray[900])};
`;

export const HeaderBarSign = styled.span<{ $bad: boolean }>`
  font-size: 14px;
  font-weight: 700;
  line-height: 1;
  color: ${({ $bad, theme }) => ($bad ? theme.color.red[500] : theme.color.gray[900])};
  ${({ $bad, theme }) => $bad && `border-radius: 2px; background-color: ${theme.color.red[100]};`}
`;

export const OverviewCardList = styled.div`
  display: grid;
  gap: 16px;
  max-width: 760px;
  margin-top: 16px;
`;

export const OverviewCard = styled.div<{ $hit: boolean }>`
  overflow: hidden;
  border: 1px solid ${({ $hit, theme }) => ($hit ? '#fca5a5' : theme.color.gray[200])};
  border-radius: 8px;
  background-color: #fff;
`;

export const OverviewCardHead = styled.div<{ $hit: boolean }>`
  display: flex;
  align-items: center;
  gap: 18px;
  padding: 12px 16px;
  border-bottom: 1px solid ${({ theme }) => theme.color.gray[150]};
  background-color: ${({ $hit, theme }) => ($hit ? '#fef2f2' : theme.color.gray[50])};
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.color.gray[900]};
`;

export const OverviewCardBody = styled.div`
  display: grid;
  gap: 8px;
  padding: 14px 16px 16px;
  overflow-x: auto;
`;

export const OverviewResult = styled.p`
  margin: 0;
  font-size: 14px;
  color: ${({ theme }) => theme.color.gray[900]};
`;

export const KeyGrid = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $columns }) => $columns}, auto);
  gap: 4px 2px;
  width: max-content;

  & > ${FontCheckLabel} {
    grid-column: 1 / -1;
  }
`;

export const KeyBox = styled.span<{ $moved: boolean }>`
  padding: 3px;
  border: 1.5px solid
    ${({ $moved, theme }) => ($moved ? theme.color.red[500] : theme.color.gray[200])};
  border-radius: 3px;
  font-family: monospace;
  font-size: 10px;
  text-align: center;
  color: ${({ $moved, theme }) => ($moved ? theme.color.red[500] : theme.color.gray[750])};
  background-color: ${({ $moved, theme }) => ($moved ? theme.color.red[100] : '#fff')};
`;

export const BarRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 3px;

  & > ${KeyBox} {
    width: 26px;
    padding: 4px 0;
    font-size: 14px;
    font-weight: 700;
  }
`;
